/**
 * FeedbackPromptHost · Feb 2026
 * ───────────────────────────────────────────────────────────────────
 * Single mount-point for every post-install prompt. Listens on the
 * window event `soulumo:feedback-show` (dispatched by the
 * `useFeedbackPrompts` hook after the backend decides what to ask)
 * and renders the correct lightweight card.
 *
 *   prompt = "micro"       → one rotating question (0-10 or Y/M/N)
 *   prompt = "state_shift" → "Did something shift for you?" 0-10
 *   prompt = "miss_most"   → "If Soulumo disappeared tomorrow…"
 *   prompt = "pulse"       → opens the full BetaPulseModal
 *
 * Micro/state-shift/miss-most cards are deliberately slim — one
 * question, one tap, one dismiss. They never scroll.
 */
import React, { useEffect, useState } from "react";
import BetaPulseModal from "./BetaPulseModal";

const API = process.env.REACT_APP_BACKEND_URL || "";
const TOK = {
  cream: "#f5ecd9",
  creamDim: "rgba(245,236,217,0.72)",
  creamMist: "rgba(245,236,217,0.44)",
  gold: "#e0a97a",
  goldSoft: "rgba(224,169,122,0.86)",
  goldMist: "rgba(224,169,122,0.22)",
};

function auth() {
  const t = localStorage.getItem("reclaim_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function postEvent(payload) {
  const r = await fetch(`${API}/api/beta-feedback/events`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...auth() },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    const j = await r.json().catch(() => ({}));
    throw new Error(j.detail || `HTTP ${r.status}`);
  }
  return r.json();
}

async function logOutcome(prompt_type, outcome) {
  try {
    await fetch(`${API}/api/beta-feedback/prompt-log`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...auth() },
      body: JSON.stringify({ prompt_type, outcome }),
    });
  } catch (_) {}
}

function contextPayload(ctx = {}, source = "post-install") {
  return {
    installation_id: ctx.journeyId || ctx.installationId || null,
    installation_name: ctx.installationName || null,
    category: ctx.category || null,
    experience_type: ctx.experienceType || null,
    duration_sec: ctx.durationSec || null,
    listened_sec: ctx.listenedSec || null,
    completion_pct: ctx.completionPct || null,
    voice_id: ctx.voiceId || null,
    voice_label: ctx.voiceLabel || null,
    own_voice: ctx.ownVoice || null,
    music: ctx.music || null,
    day_or_night: ctx.dayOrNight || null,
    trigger_source: source,
    device: typeof window !== "undefined" && window.innerWidth < 768 ? "mobile" : "desktop",
  };
}

export default function FeedbackPromptHost() {
  const [active, setActive] = useState(null); // {prompt, microPrompt, context}

  useEffect(() => {
    const onShow = (e) => {
      const d = (e && e.detail) || {};
      if (!d.prompt) return;
      setActive(d);
    };
    window.addEventListener("soulumo:feedback-show", onShow);

    // Allow the footer "Give feedback" link + anywhere else to open
    // the Pulse modal manually — same event, prompt="pulse".
    return () => window.removeEventListener("soulumo:feedback-show", onShow);
  }, []);

  if (!active) return null;

  const close = () => setActive(null);
  const dismiss = () => {
    logOutcome(active.prompt, "dismissed");
    close();
  };

  if (active.prompt === "pulse") {
    return (
      <BetaPulseModal
        open={true}
        onClose={close}
        context={active.context || {}}
        source="post-install"
      />
    );
  }
  if (active.prompt === "micro") {
    return (
      <MicroCard
        prompt={active.microPrompt}
        context={active.context || {}}
        onDone={close}
        onDismiss={dismiss}
      />
    );
  }
  if (active.prompt === "state_shift") {
    return (
      <StateShiftCard
        context={active.context || {}}
        onDone={close}
        onDismiss={dismiss}
      />
    );
  }
  if (active.prompt === "miss_most") {
    return (
      <MissMostCard
        context={active.context || {}}
        onDone={close}
        onDismiss={dismiss}
      />
    );
  }
  return null;
}

// ──────────────────────────────────────────────────────────────────
// Shared card chrome
function Shell({ children, onDismiss, testid }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-5"
      style={{ background: "rgba(0,0,0,0.76)", backdropFilter: "blur(8px)" }}
      onClick={onDismiss}
      role="dialog"
      aria-modal="true"
      data-testid={testid}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 sm:p-7"
        style={{
          background: "linear-gradient(180deg, rgba(10,20,40,0.98) 0%, rgba(5,11,26,1) 100%)",
          border: `1px solid ${TOK.goldMist}`,
          color: TOK.cream,
          maxHeight: "88vh",
          overflowY: "auto",
          boxShadow: "0 30px 90px rgba(0,0,0,0.55), 0 0 60px rgba(224,169,122,0.08)",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Eyebrow() {
  return (
    <div
      className="uppercase"
      style={{
        color: TOK.goldSoft,
        fontFamily: '"Manrope", sans-serif',
        fontSize: "0.58rem",
        letterSpacing: "0.36em",
      }}
    >
      ✦ Soulumo · a quiet check-in ✦
    </div>
  );
}

function CardTitle({ children }) {
  return (
    <h3
      className="mt-2 italic font-light"
      style={{
        color: TOK.cream,
        fontFamily: '"Cormorant Garamond", serif',
        fontSize: "clamp(1.3rem, 4.8vw, 1.65rem)",
        lineHeight: 1.3,
      }}
    >
      {children}
    </h3>
  );
}

function CardSub({ children }) {
  return (
    <p
      className="mt-1 italic"
      style={{
        color: TOK.creamMist,
        fontFamily: '"Cormorant Garamond", serif',
        fontSize: "0.9rem",
      }}
    >
      {children}
    </p>
  );
}

function NumberGrid({ value, onPick, testPrefix }) {
  return (
    <div className="mt-4 grid grid-cols-11 gap-1">
      {Array.from({ length: 11 }, (_, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onPick(i)}
          data-testid={`${testPrefix}-${i}`}
          style={{
            minHeight: 42,
            padding: "6px 0",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.92rem",
            fontWeight: 600,
            borderRadius: 8,
            background: value === i
              ? "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)"
              : "rgba(255,255,255,0.04)",
            color: value === i ? "#1a0f05" : TOK.creamDim,
            border: `1px solid ${value === i ? "#f5e0c5" : TOK.goldMist}`,
            cursor: "pointer",
          }}
        >
          {i}
        </button>
      ))}
    </div>
  );
}

function NotNowButton({ onClick, label = "Not now" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="feedback-not-now"
      className="mt-2 w-full py-2 italic"
      style={{
        background: "transparent",
        border: "none",
        color: TOK.creamMist,
        fontFamily: '"Cormorant Garamond", serif',
        fontSize: "0.9rem",
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────
// Micro — one rotating question
function MicroCard({ prompt, context, onDone, onDismiss }) {
  const [done, setDone] = useState(false);
  const [val, setVal] = useState(null);
  const [choose, setChoose] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const isChooseAgain = prompt && prompt.scale === "yes-maybe-no";

  const submit = async (payload) => {
    setSubmitting(true);
    try {
      await postEvent({
        type: "micro",
        scores: {
          micro_prompt_id: prompt.id,
          ...(payload.micro_score != null ? { micro_score: payload.micro_score } : {}),
          ...(payload.micro_choose_again ? { micro_choose_again: payload.micro_choose_again } : {}),
        },
        context: contextPayload(context, "post-install"),
      });
      logOutcome("micro", "submitted");
      setDone(true);
      setTimeout(onDone, 1500);
    } catch (_) {
      setSubmitting(false);
    }
  };

  if (!prompt) return null;

  return (
    <Shell onDismiss={onDismiss} testid="micro-feedback-card">
      <Eyebrow />
      {done ? (
        <div data-testid="micro-feedback-done" className="py-2">
          <CardTitle>Thank you.</CardTitle>
          <CardSub>Soulumo listens.</CardSub>
        </div>
      ) : (
        <>
          <CardTitle>{prompt.prompt}</CardTitle>
          {!isChooseAgain && <CardSub>Just a tap — no typing.</CardSub>}
          {isChooseAgain ? (
            <div className="mt-4 grid grid-cols-3 gap-2" data-testid="micro-choose-again">
              {[
                { v: "yes", label: "Yes" },
                { v: "maybe", label: "Maybe" },
                { v: "no", label: "No" },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  disabled={submitting}
                  onClick={() => { setChoose(o.v); submit({ micro_choose_again: o.v }); }}
                  data-testid={`micro-choose-${o.v}`}
                  style={{
                    minHeight: 54,
                    padding: "10px",
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: "0.95rem",
                    fontWeight: 600,
                    borderRadius: 12,
                    background: choose === o.v
                      ? "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)"
                      : "rgba(255,255,255,0.04)",
                    color: choose === o.v ? "#1a0f05" : TOK.creamDim,
                    border: `1px solid ${choose === o.v ? "#f5e0c5" : TOK.goldMist}`,
                    cursor: submitting ? "wait" : "pointer",
                  }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          ) : (
            <NumberGrid
              value={val}
              onPick={(i) => { setVal(i); submit({ micro_score: i }); }}
              testPrefix="micro-score"
            />
          )}
          <NotNowButton onClick={onDismiss} />
        </>
      )}
    </Shell>
  );
}

// ──────────────────────────────────────────────────────────────────
// State shift
function StateShiftCard({ context, onDone, onDismiss }) {
  const [val, setVal] = useState(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (score) => {
    setSubmitting(true);
    try {
      await postEvent({
        type: "state_shift",
        scores: { state_shift: score },
        context: contextPayload(context, "post-install"),
      });
      logOutcome("state_shift", "submitted");
      setDone(true);
      setTimeout(onDone, 1500);
    } catch (_) {
      setSubmitting(false);
    }
  };

  return (
    <Shell onDismiss={onDismiss} testid="state-shift-card">
      <Eyebrow />
      {done ? (
        <div className="py-2" data-testid="state-shift-done">
          <CardTitle>Noted — thank you.</CardTitle>
        </div>
      ) : (
        <>
          <CardTitle>Did something shift for you?</CardTitle>
          <CardSub>0 = nothing changed · 10 = a significant positive shift</CardSub>
          <NumberGrid
            value={val}
            onPick={(i) => { setVal(i); submit(i); }}
            testPrefix="state-shift-score"
          />
          <NotNowButton onClick={onDismiss} />
        </>
      )}
    </Shell>
  );
}

// ──────────────────────────────────────────────────────────────────
// Miss most
function MissMostCard({ context, onDone, onDismiss }) {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await postEvent({
        type: "miss_most",
        answers: { miss_most: text.trim() },
        context: contextPayload(context, "post-install"),
      });
      logOutcome("miss_most", "submitted");
      setDone(true);
      setTimeout(onDone, 1800);
    } catch (_) {
      setSubmitting(false);
    }
  };

  return (
    <Shell onDismiss={onDismiss} testid="miss-most-card">
      <Eyebrow />
      {done ? (
        <div className="py-2" data-testid="miss-most-done">
          <CardTitle>Beautifully put — thank you.</CardTitle>
        </div>
      ) : (
        <>
          <CardTitle>If Soulumo disappeared tomorrow, what would you miss most?</CardTitle>
          <CardSub>A sentence is more than enough.</CardSub>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            autoFocus
            data-testid="miss-most-text"
            style={{
              width: "100%",
              marginTop: 14,
              background: "rgba(0,0,0,0.35)",
              border: `1px solid ${TOK.goldMist}`,
              color: TOK.cream,
              borderRadius: 12,
              padding: "12px 14px",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.95rem",
              resize: "vertical",
              outline: "none",
              minHeight: 96,
            }}
          />
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !text.trim()}
            data-testid="miss-most-submit"
            className="mt-4 w-full rounded-full py-3 disabled:opacity-60"
            style={{
              background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
              border: "1px solid #f5e0c5",
              color: "#1a0f05",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.76rem",
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              fontWeight: 700,
              minHeight: 46,
              cursor: submitting ? "wait" : "pointer",
            }}
          >
            {submitting ? "Sending…" : "Send"}
          </button>
          <NotNowButton onClick={onDismiss} />
        </>
      )}
    </Shell>
  );
}
